import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import Cookies from 'js-cookie'
import { AGENT_COOKIE_NAME } from '@/constants'

import {
  AlertBox,
  SelectBox,
  Icons,
  Button,
  Field,
  Spinner
} from 'msp-components'
import {
  convertDateToISODate,
  convertISODateToGermanDate,
  useAccount,
  useCookie
} from 'msp-integrations'

import getAddMachineFields from './AccountMachineRegister.fields'
import translations from './AccountMachineRegister.constants'
import * as S from './AccountMachineRegister.styles'

export type AccountMachineRegisterProps = {
  elemRef: React.MutableRefObject<AlertBox>
  hideButton?: boolean
}

const AccountMachineRegister = ({
  elemRef,
  hideButton
}: AccountMachineRegisterProps) => {
  const form = useForm()
  const { eventTracking } = useCookie()
  const { formatMessage } = useIntl()
  const [isPrefilled, setIsPrefilled] = useState(false)
  const selectUnregisteredMachine = {
    id: 'none',
    value: formatMessage(translations.selectUnregisteredMachine)
  }
  const [errorMessage, setErrorMessage] = useState(null)
  const fieldConfig = getAddMachineFields()
  const [registeredMachineSerialNo, setRegisteredMachineSerialNo] =
    useState(null)
  const {
    machines: {
      isProcessingAction,
      addMachine,
      hasRegisterableMachines,
      registerableMachines,
      machines
    }
  } = useAccount()

  useEffect(() => {
    if (registeredMachineSerialNo) {
      const machine = machines?.find(
        (m) => m.SerialNo === registeredMachineSerialNo
      )
      if (machine) {
        eventTracking('maschineRegistration', {
          machine: machine
        })
      }
      setRegisteredMachineSerialNo(null)
    }
  }, [registeredMachineSerialNo])

  const registerableMachinesOptions = registerableMachines.map(
    ({ serialnumber, machineNameAndModel }) => ({
      id: serialnumber,
      value: `${machineNameAndModel} (${serialnumber})`
    })
  )

  registerableMachinesOptions.unshift(selectUnregisteredMachine)

  const resetForm = () => {
    setIsPrefilled(false)
    form.clearErrors()
    form.reset({
      SerialNo: '',
      purchaseDate: ''
    })
  }

  const cancelRegistration = async () => {
    resetForm()
    setErrorMessage(null)
    elemRef?.current.closeAlert()
  }

  const submitForm = async (data: any) => {
    try {
      form.clearErrors()

      const payload = {
        SerialNo: data.SerialNo,
        purchaseDate: convertDateToISODate(data.purchaseDate),
        installDate: new Date(),
        agentID: Cookies.get(AGENT_COOKIE_NAME) || ''
      }

      setErrorMessage(null)
      await addMachine({ body: payload })
        .then(() => {
          setRegisteredMachineSerialNo(data.SerialNo)
          resetForm()
          elemRef?.current.closeAlert()
        })
        .catch((error) => {
          setErrorMessage(error?.message || 'There was a technical issue')
        })
    } catch (error) {
      form.setError('global', { type: 'manual', message: error.message })
    }
  }

  const handleRegisterableMachineSelect = (event) => {
    const serialNo = event?.target?.value

    if (serialNo === 'none') {
      resetForm()
    } else {
      setIsPrefilled(true)
      const [selectedMachine] = registerableMachines.filter(
        (data) => data.serialnumber === serialNo
      )

      if (selectedMachine) {
        form.reset({
          SerialNo: selectedMachine.serialnumber,
          purchaseDate: convertISODateToGermanDate(selectedMachine.purchaseDate)
        })
      }
    }
  }

  return (
    <>
      {!hideButton && (
        <Button
          type={'secondary'}
          variant='outline'
          className={S.AddNewMachineButton}
          isDisabled={isProcessingAction}
          onClick={() => !isProcessingAction && elemRef?.current.showAlert()}
        >
          {formatMessage(translations.registerMachine)}
        </Button>
      )}
      <AlertBox
        type='standard'
        ref={elemRef}
        extendedWrapperStyle={S.AlertBoxWrapper}
        headlineColor={S.ConfirmationHeadline}
        title={formatMessage(translations.registerMachineTitle)}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      >
        {errorMessage && (
          <div className={S.ErrorMessageWrapper}>{errorMessage}</div>
        )}
        {form.formState?.isSubmitting && <Spinner fullScreen />}

        <form
          id={'machine-registration'}
          onSubmit={form.handleSubmit(submitForm)}
          autoComplete='off'
        >
          <div className={S.FormFieldsWrapper}>
            {hasRegisterableMachines && (
              <>
                <SelectBox
                  id={'registerable-machine'}
                  className={S.RegisterableMachinesSelectBox}
                  onChange={handleRegisterableMachineSelect}
                  disabled={form.formState.isSubmitting}
                  options={registerableMachinesOptions}
                />
                <div className={S.OrSeparator}>
                  {formatMessage(translations.or)}
                </div>
              </>
            )}
            <Field
              control={form.control}
              config={fieldConfig.SerialNo}
              error={form.formState.errors?.SerialNo}
              readOnly={isPrefilled}
              isDisabled={form.formState.isSubmitting}
            />
            <Field
              className={S.FormFieldItem}
              control={form.control}
              config={fieldConfig.purchaseDate}
              error={form.formState.errors?.purchaseDate}
              readOnly={isPrefilled}
              isDisabled={form.formState.isSubmitting}
            />
          </div>
          <div className={S.ConfirmationButtonsWrapper}>
            <Button
              type={'secondary'}
              variant='outline'
              className={S.ConfirmationCancelButton}
              isDisabled={form.formState?.isSubmitting}
              onClick={cancelRegistration}
            >
              {formatMessage(translations.registerMachineCancel)}
            </Button>
            <Button
              isSubmit
              type='secondary'
              isDisabled={form.formState?.isSubmitting}
            >
              {formatMessage(translations.registerMachineConfirm)}
            </Button>
          </div>
        </form>
      </AlertBox>
    </>
  )
}

export default AccountMachineRegister
